import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { WizardSkeleton, WizardStepFooter } from '../wizard';
import {
  useRepaymentPlanQuery,
  useSaveRepaymentPlanMutation,
  useSimulateExtraMutation,
} from '../../features/liabilities/queries';
import { useCreateGoalMutation } from '../../features/goals/queries';
import { paths } from '../../routes/paths';
import pt from '../../locales/pt';
import { extractMutationError } from '../../utils/apiError';
import {
  formatExtraForApi,
  formatPayoffDatePt,
  suggestHalfExtraMonthly,
  targetDateFromPayoffMonths,
} from '../../utils/debtPlanUtils';
import { formatFreeEta } from '../../lib/debtDisplayUtils';
import { DebtContextStep } from './wizard/DebtContextStep';
import { DebtDoneStep } from './wizard/DebtDoneStep';
import { DebtMethodStep } from './wizard/DebtMethodStep';
import { DebtSimulateStep } from './wizard/DebtSimulateStep';
import { DebtVaultStep } from './wizard/DebtVaultStep';

const ALL_STEPS = ['context', 'method', 'simulate', 'vault', 'done'];

function freeDateFromSimulation(simulation, fallbackMonths) {
  if (simulation && !simulation.requires_more_data) {
    if (simulation.predicted_payoff_date) {
      return (
        formatFreeEta(simulation.predicted_payoff_date) ||
        formatPayoffDatePt(simulation.predicted_payoff_date)
      );
    }
    const months = simulation.accelerated_payoff_months;
    if (months != null) {
      return formatFreeEta(months) || formatPayoffDatePt(targetDateFromPayoffMonths(months));
    }
  }
  if (fallbackMonths != null) return formatFreeEta(fallbackMonths);
  return '—';
}

/**
 * Wizard de plano de libertação (esqueleto-mãe).
 * Usado na rota /dividas/plano — não é Dialog.
 */
export function DebtRepaymentPlanWizard({ activeDebts, existingPlan }) {
  const navigate = useNavigate();
  const skipMethod = activeDebts.length <= 1;
  const steps = useMemo(
    () => (skipMethod ? ALL_STEPS.filter((s) => s !== 'method') : ALL_STEPS),
    [skipMethod],
  );
  const copy = pt.liabilities.repaymentWizard;

  const [stepIndex, setStepIndex] = useState(0);
  const [direction, setDirection] = useState('forward');
  const [method, setMethod] = useState(existingPlan?.method || 'avalanche');
  const [planSnapshot, setPlanSnapshot] = useState(null);
  const [priorityId, setPriorityId] = useState(
    existingPlan?.priority_liability || null,
  );
  const [simulation, setSimulation] = useState(null);
  const [extra, setExtra] = useState(0);
  const [createVault, setCreateVault] = useState(true);
  const planInitialized = useRef(false);
  const extraInitialized = useRef(false);

  const step = steps[stepIndex];
  const isDone = step === 'done';

  const methodStepOpen = step === 'method';
  const { data: avalancheImpact, isPending: avPending } = useRepaymentPlanQuery(
    'avalanche',
    { enabled: methodStepOpen || step === 'simulate' || step === 'vault' },
  );
  const { data: snowballImpact, isPending: snPending } = useRepaymentPlanQuery(
    'snowball',
    { enabled: methodStepOpen },
  );
  const { data: selectedPlan } = useRepaymentPlanQuery(method, {
    enabled:
      step === 'simulate' ||
      step === 'vault' ||
      step === 'method' ||
      (skipMethod && (step === 'context' || step === 'simulate')),
  });

  const methodImpacts = useMemo(
    () => ({ avalanche: avalancheImpact, snowball: snowballImpact }),
    [avalancheImpact, snowballImpact],
  );

  const simulate = useSimulateExtraMutation();
  const savePlan = useSaveRepaymentPlanMutation();
  const createGoal = useCreateGoalMutation();

  const priorityLiability = useMemo(() => {
    if (!priorityId) return null;
    return activeDebts.find((d) => String(d.id) === String(priorityId)) || null;
  }, [activeDebts, priorityId]);

  const suggestedHalf = priorityLiability
    ? suggestHalfExtraMonthly(priorityLiability)
    : 0;

  const sliderMax = Math.max(500, Math.ceil((suggestedHalf * 4) / 10) * 10 || 500);

  const baselineMonths = useMemo(() => {
    const plan = planSnapshot || selectedPlan || avalancheImpact;
    const items = plan?.items || [];
    let max = 0;
    for (const item of items) {
      const m = Number(item.payoff_months);
      if (!Number.isNaN(m) && m > max) max = m;
    }
    return max > 0 ? max : null;
  }, [planSnapshot, selectedPlan, avalancheImpact]);

  useEffect(() => {
    setMethod(existingPlan?.method || 'avalanche');
    setPriorityId(existingPlan?.priority_liability || null);
    const fromPlan = existingPlan?.extra_monthly_amount;
    if (fromPlan != null && Number(fromPlan) >= 0) {
      setExtra(Math.round(Number(fromPlan)));
    }
    // Hydrate once on mount — do not reset after save (activePlan id changes).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    simulate.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedPlan?.items?.length) return;
    if (
      step !== 'simulate' &&
      step !== 'vault' &&
      step !== 'method' &&
      !(skipMethod && step === 'context')
    ) {
      return;
    }
    const nextPriority = selectedPlan.items[0].liability_id;
    setPlanSnapshot((prev) => {
      if (prev?.method === selectedPlan.method && prev?.items?.length === selectedPlan.items.length) {
        return prev;
      }
      return selectedPlan;
    });
    setPriorityId((current) => current ?? nextPriority);
    planInitialized.current = true;
  }, [selectedPlan, step, skipMethod]);

  useEffect(() => {
    if (step !== 'simulate') {
      extraInitialized.current = false;
      return;
    }
    if (!priorityLiability || extraInitialized.current) return;
    extraInitialized.current = true;
    const fromPlan = existingPlan?.extra_monthly_amount;
    if (fromPlan != null && Number(fromPlan) >= 0) {
      setExtra(Math.round(Number(fromPlan)));
      return;
    }
    if (suggestedHalf > 0) {
      setExtra(Math.round(suggestedHalf));
    }
  }, [step, priorityLiability, existingPlan?.extra_monthly_amount, suggestedHalf]);

  const parsedExtra = formatExtraForApi(String(extra).replace('.', ','));
  const extraNumeric = Number(extra) || 0;

  useEffect(() => {
    if (step !== 'simulate' || !priorityId) return;

    if (suggestedHalf <= 0 && !priorityLiability?.monthly_payment) {
      setSimulation({ requires_more_data: true });
      return;
    }

    if (extraNumeric <= 0) {
      setSimulation({
        requires_more_data: false,
        baseline_payoff_months: baselineMonths,
        accelerated_payoff_months: baselineMonths,
        months_saved: 0,
        interest_saved_eur: '0',
        predicted_payoff_date:
          baselineMonths != null ? targetDateFromPayoffMonths(baselineMonths) : null,
      });
      return;
    }

    const timer = window.setTimeout(async () => {
      try {
        const result = await simulate.mutateAsync({
          id: priorityId,
          payload: { extra_amount: parsedExtra || '0.00', mode: 'monthly' },
        });
        setSimulation(result);
      } catch (err) {
        toast.error(extractMutationError(err, pt.common.retry));
      }
    }, 400);

    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, priorityId, extraNumeric, parsedExtra, baselineMonths]);

  const goTo = useCallback((nextIndex, dir) => {
    setDirection(dir);
    setStepIndex(nextIndex);
  }, []);

  const goNext = () => {
    if (stepIndex < steps.length - 1) {
      goTo(stepIndex + 1, 'forward');
    }
  };

  const goBack = () => {
    if (stepIndex > 0) {
      goTo(stepIndex - 1, 'back');
    }
  };

  const finish = async (withVault) => {
    const snapshot = planSnapshot || selectedPlan;
    const priority = priorityId || snapshot?.items?.[0]?.liability_id;
    if (!snapshot?.items?.length || !priority) {
      toast.error(pt.liabilities.plan.empty);
      return;
    }

    let smartGoalId = null;
    let updateExistingGoalId = null;

    try {
      if (
        withVault &&
        simulation &&
        !simulation.requires_more_data &&
        extraNumeric > 0
      ) {
        const months = simulation.accelerated_payoff_months || 1;
        const targetAmount = (extraNumeric * months).toFixed(2);
        const targetDate = targetDateFromPayoffMonths(months);
        const liability = activeDebts.find((d) => String(d.id) === String(priority));
        const name = copy.vaultGoalName.replace('{name}', liability?.name || '');

        if (existingPlan?.smart_goal) {
          smartGoalId = existingPlan.smart_goal;
          updateExistingGoalId = existingPlan.smart_goal;
        } else {
          const goal = await createGoal.mutateAsync({
            name,
            measurement_type: 'monetary',
            target_amount: targetAmount,
            target_date: targetDate,
            budget_allocation: 'lifestyle',
            description: copy.vaultDescription,
          });
          smartGoalId = goal.id;
        }
      }

      await savePlan.mutateAsync({
        method,
        priority_liability_id: priority,
        extra_monthly_amount:
          extraNumeric > 0 ? parsedExtra || extraNumeric.toFixed(2) : null,
        extra_mode: 'monthly',
        simulation_snapshot: simulation || {},
        plan_snapshot: snapshot,
        smart_goal_id: smartGoalId,
        update_existing_goal_id: updateExistingGoalId,
      });

      toast.success(copy.saved);
      goTo(steps.indexOf('done'), 'forward');
    } catch (err) {
      toast.error(extractMutationError(err, pt.common.retry));
    }
  };

  const onPrimary = () => {
    if (step === 'vault') {
      finish(createVault);
      return;
    }
    if (step === 'done') {
      navigate(paths.dividas);
      return;
    }
    goNext();
  };

  const onSkipVault = () => {
    setCreateVault(false);
    finish(false);
  };

  const isPending =
    simulate.isPending || savePlan.isPending || createGoal.isPending;

  const footerLabel = useMemo(() => {
    if (step === 'context') return copy.context.cta;
    if (step === 'vault') {
      return createVault ? copy.vault.createAndFinish : copy.vault.finishWithout;
    }
    if (step === 'done') return copy.done.cta;
    return copy.continue;
  }, [step, createVault, copy]);

  const primaryDisabled =
    (step === 'simulate' && simulation?.requires_more_data) ||
    (step === 'method' && (avPending || snPending)) ||
    (step === 'vault' &&
      !planSnapshot?.items?.length &&
      !selectedPlan?.items?.length);

  const freeDate = freeDateFromSimulation(simulation, baselineMonths);
  const interestSaved = Math.round(Number(simulation?.interest_saved_eur) || 0);
  const methodLabel =
    method === 'snowball'
      ? copy.methods.snowball.benefitLabel
      : copy.methods.avalanche.benefitLabel;

  const doneSummary = [
    { label: copy.done.strategy, value: methodLabel },
    {
      label: copy.done.extraMonthly,
      value: copy.done.extraMonthlyValue.replace(
        '{amount}',
        Math.round(extraNumeric).toLocaleString('pt-PT'),
      ),
      accent: true,
    },
    {
      label: copy.done.interestSaved,
      value: copy.done.interestSavedValue.replace(
        '{amount}',
        interestSaved.toLocaleString('pt-PT'),
      ),
    },
  ];

  return (
    <WizardSkeleton
      appChrome
      wizardTitle={copy.title}
      step={stepIndex + 1}
      total={steps.length}
      onBack={goBack}
      showBack={!isDone && stepIndex > 0}
      direction={direction}
      loading={savePlan.isPending || createGoal.isPending}
      showProgress={!isDone}
      showPercent={false}
      footer={
        <WizardStepFooter
          primaryLabel={footerLabel}
          onPrimary={onPrimary}
          onSkip={step === 'vault' ? onSkipVault : undefined}
          skipLabel={copy.vault.skip}
          loading={isPending && step === 'vault'}
          disabled={primaryDisabled}
        />
      }
    >
      {step === 'context' ? <DebtContextStep /> : null}
      {step === 'method' ? (
        <DebtMethodStep
          value={method}
          onChange={(next) => {
            planInitialized.current = false;
            setMethod(next);
          }}
          impacts={methodImpacts}
          loading={avPending || snPending}
        />
      ) : null}
      {step === 'simulate' ? (
        <DebtSimulateStep
          extra={extra}
          onExtraChange={setExtra}
          suggestedHalf={suggestedHalf}
          onUseHalf={() => setExtra(Math.round(suggestedHalf))}
          simulation={simulation}
          simulating={simulate.isPending && !simulation}
          sliderMax={sliderMax}
        />
      ) : null}
      {step === 'vault' ? (
        <DebtVaultStep
          extraAmount={extraNumeric}
          enabled={createVault}
          onToggle={setCreateVault}
        />
      ) : null}
      {step === 'done' ? (
        <DebtDoneStep freeDate={freeDate} summary={doneSummary} />
      ) : null}
    </WizardSkeleton>
  );
}

export default DebtRepaymentPlanWizard;
